import { Alert, Button, Card, CardContent, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  DEFAULT_SERP_COLORS,
  DEFAULT_SERP_MAP_STYLE,
  SERP_COLOR_LABELS,
  colorInk,
  mixColor,
  resolveSerpColors,
  type SerpMapView,
  type SerpMapStyle,
} from '../../../api/src/fakes/serp-appearance';
import { CURRENT_CLASSIFICATIONS } from '../../../api/src/fakes/serp-classification';
import { api } from '../api/client';
import { useWorkspace } from '../workspace-context';
import { useCanEdit } from './EditorOnly';
import { Box, Stack, Typography } from './mui-system';
import { SerpMapViewSelect } from './SerpMapViewSelect';
import { SerpMapStyleSelect } from './SerpMapStyleSelect';
import { fakeError } from './fakes-presentation';
export function SerpAppearanceCard() {
  const { activeWorkspace: workspace, refreshWorkspaces } = useWorkspace();
  const canEdit = useCanEdit('fakes.settings');
  const [colors, setColors] = useState(() => resolveSerpColors(workspace?.serpColors));
  const [view, setView] = useState<SerpMapView | null>(null);
  const [mapStyle, setMapStyle] = useState<SerpMapStyle>(DEFAULT_SERP_MAP_STYLE);
  const [revision, setRevision] = useState(workspace?.serpColorsRevision ?? 0);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    api
      .userPreferences()
      .then((p) => {
        if (live) {
          setView(p.serpMapView ?? 'bricks');
          setMapStyle(p.serpMapStyle ?? DEFAULT_SERP_MAP_STYLE);
        }
      })
      .catch((e) => {
        if (live) setError(fakeError(e));
      });
    return () => {
      live = false;
    };
  }, []);
  async function saveColors() {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const saved = await api.saveSerpColors(workspace!.id, colors, revision);
      setRevision(saved.serpColorsRevision ?? 0);
      await refreshWorkspaces();
      setNotice('Цвета сохранены для всех групп и новых отчётов.');
    } catch (e) {
      setError(fakeError(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack gap={2}>
          <Typography variant="h3">Карта выдачи: вид и цвета</Typography>
          <Typography color="text.secondary" fontSize={13}>
            Вид карты и оформление плиток — ваши настройки. Цвета общие для рабочего пространства:
            все группы, матрицы, heatmap, новые HTML-отчёты и картинки для Telegram. Сохранённые
            отчёты сохраняют свою палитру.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
            <SerpMapViewSelect
              value={view ?? 'bricks'}
              disabled={view === null || saving}
              onChange={async (next) => {
                setSaving(true);
                setError('');
                setNotice('');
                try {
                  const saved = await api.saveUserPreferences({ serpMapView: next });
                  setView(saved.serpMapView ?? 'bricks');
                  setNotice('Вид карты сохранён.');
                } catch (e) {
                  setError(fakeError(e));
                } finally {
                  setSaving(false);
                }
              }}
            />
            <SerpMapStyleSelect
              value={mapStyle}
              disabled={view === null || saving}
              onChange={async (next) => {
                setSaving(true);
                setError('');
                setNotice('');
                try {
                  const saved = await api.saveUserPreferences({ serpMapStyle: next });
                  setMapStyle(saved.serpMapStyle ?? DEFAULT_SERP_MAP_STYLE);
                  setNotice(
                    'Оформление плиток сохранено. Стиль Telegram выбирается в настройках отчётов.',
                  );
                } catch (e) {
                  setError(fakeError(e));
                } finally {
                  setSaving(false);
                }
              }}
            />
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {CURRENT_CLASSIFICATIONS.map((key) => (
              <TextField
                key={key}
                label={SERP_COLOR_LABELS[key]}
                type="color"
                value={colors[key]}
                disabled={!canEdit || saving}
                onChange={(e) => {
                  setNotice('');
                  setColors({ ...colors, [key]: e.target.value });
                }}
                sx={{ width: 180 }}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'aria-label': SERP_COLOR_LABELS[key] },
                }}
              />
            ))}
          </Stack>
          <Typography fontSize={12}>
            Предпросмотр: сверху — кирпичики, снизу — мягкая заливка ячеек под текстом.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} aria-label="Предпросмотр палитры">
            {CURRENT_CLASSIFICATIONS.map((key) => (
              <Box key={key} sx={{ width: 145 }}>
                <Box
                  sx={{
                    height: 15,
                    bgcolor: colors[key],
                    border: '1px solid #8885',
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1,
                    fontSize: 12,
                    bgcolor: mixColor(colors[key], '#101829', 0.18),
                    color: mixColor(colors[key], '#ffffff', 0.65),
                    borderRadius: 1,
                  }}
                >
                  {SERP_COLOR_LABELS[key]}
                </Box>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 0.5,
                    fontSize: 10,
                    bgcolor: colors[key],
                    color: colorInk(colors[key]),
                  }}
                >
                  {colors[key].toUpperCase()}
                </Box>
              </Box>
            ))}
          </Stack>
          {canEdit ? (
            <Stack direction="row" gap={1}>
              <Button variant="contained" disabled={saving} onClick={() => void saveColors()}>
                Сохранить цвета
              </Button>
              <Button
                disabled={saving}
                onClick={() => {
                  setColors({ ...DEFAULT_SERP_COLORS });
                  setNotice('Образцы восстановлены. Нажмите «Сохранить цвета».');
                }}
              >
                Сбросить к образцам
              </Button>
            </Stack>
          ) : (
            <Typography fontSize={12} color="text.secondary">
              Менять общую палитру могут редакторы рабочего пространства.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
